// Sections for the parent-company homepage. Everything here is drawn (no product
// screenshots): an agent orbit, tabbed agent families, a connected graph that plays
// through three steps, and a rail of resource cards.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, LayoutGroup, motion, useInView, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  HelpCircle,
  MessageSquare,
  PenLine,
  Plug,
  Send,
  Sparkles,
  User,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { SmartLink } from "@/components/Header";
import { integrationByKey } from "@/data/site";

const isPrerender = () => typeof navigator !== "undefined" && /HeadlessChrome/.test(navigator.userAgent);
const BLUE = "hsl(216 84% 56%)";
const GREEN = "hsl(150 55% 45%)";
const AMBER = "hsl(38 95% 55%)";

/* ── Hero: agents orbiting the ClearMakes mark ────────────────────────────── */
const ORBIT: { icon: LucideIcon; label: string; tint: string; x: number; y: number }[] = [
  { icon: Plug, label: "Connect apps", tint: "bg-[hsl(216_84%_52%)]", x: 12, y: 30 },
  { icon: HelpCircle, label: "Smart FAQ", tint: "bg-[hsl(0_70%_55%)]", x: 27, y: 78 },
  { icon: PenLine, label: "Draft a reply", tint: "bg-[hsl(32_90%_50%)]", x: 88, y: 28 },
  { icon: Send, label: "Post to Slack", tint: "bg-[hsl(262_55%_55%)]", x: 75, y: 80 },
  { icon: Zap, label: "Workflows", tint: "bg-[hsl(150_55%_38%)]", x: 50, y: 6 },
];

export const AgentOrbit = () => (
  <div aria-hidden className="relative mx-auto aspect-[16/7] w-full max-w-4xl">
    <svg viewBox="0 0 100 43.75" className="absolute inset-0 h-full w-full" preserveAspectRatio="none">
      {ORBIT.map((o) => (
        <line key={o.label} x1="50" y1="21.9" x2={o.x} y2={o.y * 0.4375} stroke="hsl(0 0% 85%)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeDasharray="5 5" className="cm-dash" />
      ))}
    </svg>
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
      <span className="cm-ping absolute inset-0 rounded-3xl border border-[hsl(216_84%_52%/0.35)]" />
      <span className="relative flex h-20 w-20 items-center justify-center rounded-3xl border border-border bg-background shadow-[0_20px_50px_-20px_hsl(0_0%_0%/0.3)] sm:h-24 sm:w-24">
        <img src="/logo-mark-256.png" alt="" className="h-11 w-11 sm:h-14 sm:w-14" />
      </span>
    </div>
    {ORBIT.map((o, i) => (
      <span
        key={o.label}
        className="cm-float absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full border border-border bg-background py-1.5 pl-1.5 pr-3.5 text-[13px] font-medium text-foreground shadow-[0_10px_30px_-16px_hsl(0_0%_0%/0.3)] sm:text-[14px]"
        style={{ left: `${o.x}%`, top: `${o.y}%`, animationDelay: `${-i * 1.3}s` }}
      >
        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-white ${o.tint}`}>
          <o.icon className="h-3.5 w-3.5" />
        </span>
        {o.label}
      </span>
    ))}
  </div>
);

/* ── Agent families, as a segmented control ───────────────────────────────── */
interface Family {
  key: string;
  tab: string;
  title: string;
  body: string;
  href: string;
  color: string;
  icon: LucideIcon;
  includes: { label: string; href: string }[];
  lines: string[];
}

const FAMILIES: Family[] = [
  {
    key: "automate",
    tab: "Automate",
    title: "Workflows that act on new messages.",
    body: "When an email arrives, a workflow can summarise it, draft a reply, post it to Slack or add a note in HubSpot. No code needed.",
    href: "/workflows",
    color: "hsl(150 55% 38%)",
    icon: Zap,
    includes: [{ label: "Workflows", href: "/workflows" }],
    lines: ["When: new email in Gmail", "Then: summarise it", "Then: post to #sales in Slack"],
  },
  {
    key: "connect",
    tab: "Connect",
    title: "Connect the apps you already use.",
    body: "Gmail, Outlook, Slack, Teams, Notion, HubSpot and more. Connect each one in a few clicks and disconnect it any time.",
    href: "/integrations",
    color: "hsl(216 84% 52%)",
    icon: Plug,
    includes: [
      { label: "Apps", href: "/integrations" },
      { label: "Gmail", href: "/gmail-integration" },
      { label: "Slack", href: "/slack-integration" },
    ],
    lines: ["Gmail · connected", "Slack · connected", "HubSpot · connected"],
  },
  {
    key: "answer",
    tab: "Answer",
    title: "Smart FAQ answers the questions you get every day.",
    body: "Write your answers once. Smart FAQ uses them to answer the same questions when they come in again.",
    href: "/smart-faq",
    color: "hsl(32 90% 50%)",
    icon: HelpCircle,
    includes: [{ label: "Smart FAQ", href: "/smart-faq" }],
    lines: ["Question · when do you ship?", "Answer found · shipping", "Reply drafted from your answer"],
  },
];

const FamilyArt = ({ f }: { f: Family }) => (
  <div aria-hidden className="relative mx-auto aspect-[5/4] w-full max-w-[520px]">
    <motion.div
      className="absolute inset-y-[6%] left-[8%] right-0 rounded-[6px]"
      style={{ background: f.color, clipPath: "polygon(0 8%, 100% 0, 100% 92%, 0 100%)" }}
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    />
    <div className="absolute inset-y-[16%] left-0 right-[10%] flex flex-col justify-center gap-3 rounded-2xl border border-border bg-background p-5 shadow-[0_30px_60px_-30px_hsl(0_0%_0%/0.35)] sm:p-7">
      <span className="flex items-center gap-2.5 text-[15px] font-semibold text-foreground">
        <span className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ background: f.color }}>
          <f.icon className="h-4 w-4" />
        </span>
        {f.tab} agent
      </span>
      {f.lines.map((l, i) => (
        <motion.span
          key={l}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25 + i * 0.16, duration: 0.35 }}
          className="flex items-center gap-2.5 rounded-lg bg-[hsl(40_9%_96%)] px-3.5 py-2.5 text-[14px] text-foreground"
        >
          <Check className="h-3.5 w-3.5 shrink-0" style={{ color: f.color }} strokeWidth={3} /> {l}
        </motion.span>
      ))}
    </div>
    <motion.span
      className="absolute right-[4%] top-[8%] rounded-full bg-foreground px-3.5 py-1.5 text-[13px] font-medium text-background"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.5, type: "spring", stiffness: 300, damping: 18 }}
    >
      Working
    </motion.span>
  </div>
);

export const AgentFamilies = () => {
  const [key, setKey] = useState(FAMILIES[0].key);
  const f = FAMILIES.find((x) => x.key === key)!;
  return (
    <div>
      <LayoutGroup id="families">
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div role="tablist" aria-label="Agent families" className="mx-auto flex w-max gap-1 rounded-full bg-[hsl(40_9%_95%)] p-1.5">
            {FAMILIES.map((t) => (
              <button
                key={t.key}
                role="tab"
                type="button"
                aria-selected={t.key === key}
                onClick={() => setKey(t.key)}
                className={`relative rounded-full px-5 py-2.5 text-[15px] font-medium transition-colors duration-200 sm:px-7 ${t.key === key ? "text-background" : "text-foreground hover:bg-[hsl(40_9%_90%)]"}`}
              >
                {t.key === key && <motion.span layoutId="family-pill" className="absolute inset-0 rounded-full bg-foreground" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
                <span className="relative">{t.tab}</span>
              </button>
            ))}
          </div>
        </div>
      </LayoutGroup>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={f.key}
          role="tabpanel"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25 }}
          className="mt-14 grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16"
        >
          <div>
            <p className="text-[22px] tracking-[-0.01em] text-foreground">
              <span className="font-bold">{f.tab}</span> agents
            </p>
            <h3 className="mt-5 text-[2rem] font-semibold leading-[1.12] tracking-[-0.03em] text-foreground sm:text-[2.6rem]">{f.title}</h3>
            <p className="cm-lead mt-5 max-w-xl">{f.body}</p>
            <p className="mt-10 text-[13px] font-semibold uppercase tracking-[0.08em] text-foreground">Includes</p>
            <ul className="mt-4 flex flex-wrap gap-x-7 gap-y-3">
              {f.includes.map((i) => (
                <li key={i.label}>
                  <SmartLink href={i.href} className="group inline-flex items-center gap-2.5 text-[18px] text-foreground">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg text-white transition-transform duration-200 group-hover:-translate-y-0.5" style={{ background: f.color }}>
                      <f.icon className="h-4 w-4" />
                    </span>
                    {i.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
            <SmartLink href={f.href} className="group mt-10 inline-flex items-center gap-1.5 text-[16px] font-medium text-[hsl(216_84%_45%)]">
              Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </SmartLink>
          </div>
          <FamilyArt f={f} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

/* ── The connected graph ──────────────────────────────────────────────────── */
type NodeDef = { id: string; x: number; y: number; g: 0 | 1 | 2; logo?: string; icon?: LucideIcon; pill?: string; big?: boolean };
const NODES: NodeDef[] = [
  { id: "gmail", x: 8, y: 26, g: 0, logo: "gmail" },
  { id: "slack", x: 5, y: 62, g: 0, logo: "slack" },
  { id: "outlook", x: 18, y: 84, g: 0, logo: "outlook" },
  { id: "teams", x: 24, y: 12, g: 0, logo: "teams" },
  { id: "msg", x: 19, y: 46, g: 0, pill: "New message", icon: MessageSquare },
  { id: "sum", x: 45, y: 22, g: 1, icon: Sparkles, big: true },
  { id: "pri", x: 53, y: 56, g: 1, icon: Zap, big: true },
  { id: "rep", x: 42, y: 84, g: 1, icon: PenLine },
  { id: "faq", x: 64, y: 30, g: 1, icon: HelpCircle },
  { id: "find", x: 58, y: 8, g: 1, logo: "notion" },
  { id: "draft", x: 63, y: 82, g: 1, pill: "Draft", icon: PenLine },
  { id: "you", x: 81, y: 48, g: 2, icon: User, big: true },
  { id: "ok", x: 91, y: 16, g: 2, pill: "Approve", icon: Check },
  { id: "cal", x: 94, y: 70, g: 2, logo: "hubspot" },
  { id: "sent", x: 78, y: 88, g: 2, pill: "Sent", icon: Send },
];
const EDGES: [string, string, boolean?][] = [
  ["gmail", "msg"], ["slack", "msg"], ["outlook", "msg"], ["teams", "msg", true], ["teams", "gmail"],
  ["msg", "sum"], ["msg", "pri", true],
  ["sum", "pri"], ["sum", "find", true], ["sum", "faq"], ["pri", "rep"], ["pri", "draft"], ["faq", "pri", true], ["rep", "draft"],
  ["pri", "you"], ["draft", "you", true], ["faq", "you"],
  ["you", "ok"], ["you", "cal", true], ["you", "sent"], ["ok", "cal"],
];
const GROUP_COLOR = [BLUE, GREEN, AMBER];
const STEPS = [
  { title: "It all starts with a new message…", body: "Connect Gmail, Outlook, Slack and Teams. When a new message arrives, your workflows see it. Nothing to forward, nothing to copy across." },
  { title: "…then workflows do the work…", body: "A workflow can summarise the message, draft a reply from your Smart FAQ answers, create a Notion page or post to Slack." },
  { title: "…and you stay in control.", body: "You decide what each workflow does and what goes out. You can disconnect any app at any time." },
];
const STEP_MS = 5200;

export const AgentGraph = ({ href }: { href: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-120px" });
  const reduce = useReducedMotion();
  const still = reduce || isPrerender();
  const [step, setStep] = useState(still ? 3 : 0); // 3 = everything lit
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (still || !inView || step >= 3) return;
    const t = setTimeout(() => {
      setStep((s) => s + 1);
      setTick((k) => k + 1);
    }, STEP_MS);
    return () => clearTimeout(t);
  }, [still, inView, step, tick]);

  const lit = (g: number) => step >= 3 || g <= step;
  const shown = Math.min(step, 2);
  const pos = Object.fromEntries(NODES.map((n) => [n.id, n]));

  return (
    <div ref={ref} className="cm-container">
      <h2 className="max-w-2xl text-[2.2rem] font-normal leading-[1.08] tracking-[-0.03em] text-white sm:text-[3.4rem]">
        See how ClearMakes workflows <span className="font-bold">work together</span>
      </h2>

      <div aria-hidden className="relative mt-8 aspect-[16/10] w-full sm:mt-4 sm:aspect-[16/6.4]">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible">
          {EDGES.map(([a, b, dashed]) => {
            const A = pos[a], B = pos[b];
            const g = Math.max(A.g, B.g);
            const on = lit(g);
            const mx = (A.x + B.x) / 2, my = (A.y + B.y) / 2 + (A.x < B.x ? -6 : 6);
            return (
              <motion.path
                key={a + b}
                d={`M ${A.x} ${A.y} Q ${mx} ${my} ${B.x} ${B.y}`}
                fill="none"
                vectorEffect="non-scaling-stroke"
                strokeWidth={2}
                strokeDasharray={dashed ? "5 5" : undefined}
                className={dashed && on ? "cm-dash" : undefined}
                initial={false}
                animate={{ stroke: on ? GROUP_COLOR[g] : "hsl(0 0% 32%)", opacity: on ? 1 : 0.5 }}
                transition={{ duration: 0.8 }}
              />
            );
          })}
        </svg>
        {NODES.map((n, i) => {
          const on = lit(n.g);
          const c = GROUP_COLOR[n.g];
          const logo = n.logo ? integrationByKey(n.logo)?.logo : undefined;
          return (
            <span key={n.id} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${n.x}%`, top: `${n.y}%` }}>
            <motion.span
              className="block"
              initial={false}
              animate={{ scale: on ? 1 : 0.9, opacity: on ? 1 : 0.45 }}
              transition={{ duration: 0.5, delay: on ? (i % 6) * 0.06 : 0 }}
            >
              {n.pill ? (
                <span className="flex items-center gap-2 whitespace-nowrap rounded-full border-2 bg-[hsl(0_0%_7%)] px-3 py-1.5 font-mono text-[11px] text-white sm:px-4 sm:py-2 sm:text-[13px]" style={{ borderColor: on ? c : "hsl(0 0% 32%)" }}>
                  {n.icon && <n.icon className="h-3.5 w-3.5" />} {n.pill}
                </span>
              ) : n.big ? (
                <span className="flex h-12 w-12 items-center justify-center rounded-full text-[hsl(0_0%_7%)] sm:h-[5.5rem] sm:w-[5.5rem]" style={{ background: on ? c : "hsl(0 0% 32%)" }}>
                  {n.icon && <n.icon className="h-5 w-5 sm:h-9 sm:w-9" strokeWidth={1.6} />}
                </span>
              ) : logo ? (
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border-2 bg-white sm:h-11 sm:w-11 sm:rounded-xl" style={{ borderColor: on ? c : "hsl(0 0% 32%)" }}>
                  <img src={logo} alt="" className="h-4 w-4 object-contain sm:h-6 sm:w-6" />
                </span>
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border-2 bg-[hsl(0_0%_7%)] text-white sm:h-11 sm:w-11 sm:rounded-xl" style={{ borderColor: on ? c : "hsl(0 0% 32%)" }}>
                  {n.icon && <n.icon className="h-4 w-4 sm:h-5 sm:w-5" />}
                </span>
              )}
            </motion.span>
            </span>
          );
        })}
      </div>

      <div className="mt-8 min-h-[150px] max-w-2xl sm:mt-2">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={shown} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
            <p className="text-[1.6rem] font-bold tracking-[-0.02em] text-white">{STEPS[shown].title}</p>
            <p className="mt-2 text-[18px] leading-snug text-white/90 sm:text-[20px]">{STEPS[shown].body}</p>
          </motion.div>
        </AnimatePresence>
        <SmartLink href={href} className="group mt-4 inline-flex items-center gap-1.5 text-[17px] text-white">
          See workflows in ClearMakes Inbox <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </SmartLink>
      </div>

      <div className="mt-6 flex items-center gap-4">
        <div className="flex flex-1 gap-2">
          {STEPS.map((s, i) => (
            <button
              key={s.title}
              type="button"
              aria-label={`Step ${i + 1}: ${s.title}`}
              onClick={() => { setStep(i); setTick((k) => k + 1); }}
              className="h-2 flex-1 overflow-hidden rounded-full bg-white/20"
            >
              {step > i ? (
                <span className="block h-full w-full rounded-full" style={{ background: BLUE }} />
              ) : step === i && inView && !still ? (
                <motion.span key={tick} className="block h-full rounded-full" style={{ background: BLUE }} initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: STEP_MS / 1000, ease: "linear" }} />
              ) : null}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => (step >= 3 ? (setStep(0), setTick((k) => k + 1)) : setStep(3))}
          className="rounded-full border border-white/70 px-6 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-white hover:text-black"
        >
          {step >= 3 ? "Replay" : "Skip"}
        </button>
      </div>
    </div>
  );
};

/* ── Resource rail ────────────────────────────────────────────────────────── */
export interface ResourceCard {
  tag: string;
  title: string;
  cta: string;
  href: string;
  color: string; // bottom block
  art: ReactNode;
}

export const ResourceRail = ({ cards }: { cards: ResourceCard[] }) => {
  const rail = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState<{ start: boolean; end: boolean }>({ start: true, end: false });
  const update = () => {
    const el = rail.current;
    if (el) setEdge({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth > el.scrollWidth - 8 });
  };
  useEffect(update, []);
  const move = (dir: number) => rail.current?.scrollBy({ left: dir * Math.min(440, rail.current.clientWidth * 0.8), behavior: "smooth" });

  return (
    <div>
      <div ref={rail} onScroll={update} className="cm-noscroll -mx-4 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-2 sm:gap-8 scroll-px-4 sm:mx-0 sm:px-0 sm:scroll-px-0">
        {cards.map((c) => (
          <SmartLink key={c.title} href={c.href} className="group flex w-[82vw] max-w-[390px] shrink-0 snap-start flex-col overflow-hidden rounded-3xl transition-transform duration-300 hover:-translate-y-1.5 sm:w-[390px]">
            <div className="relative aspect-square overflow-hidden bg-[hsl(0_0%_7%)]">{c.art}</div>
            <div className="flex flex-1 flex-col p-7 text-white sm:p-8" style={{ background: c.color }}>
              <span className="w-fit rounded bg-white/90 px-2 py-0.5 text-[12px] font-bold uppercase tracking-[0.06em]" style={{ color: c.color }}>{c.tag}</span>
              <span className="mt-5 flex-1 text-[1.35rem] font-semibold leading-snug tracking-[-0.015em]">{c.title}</span>
              <span className="mt-7 inline-flex items-center gap-1.5 text-[17px] font-medium">
                {c.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </SmartLink>
        ))}
      </div>
      <div className="mt-8 flex justify-end gap-2">
        {[-1, 1].map((d) => {
          const off = d < 0 ? edge.start : edge.end;
          return (
            <button
              key={d}
              type="button"
              aria-label={d < 0 ? "Previous" : "Next"}
              disabled={off}
              onClick={() => move(d)}
              className={`flex h-14 w-14 items-center justify-center rounded-full text-white transition-[background-color,transform] duration-200 active:scale-95 ${off ? "bg-[hsl(0_0%_72%)]" : "bg-foreground hover:bg-[hsl(0_0%_22%)]"}`}
            >
              {d < 0 ? <ArrowLeft className="h-6 w-6" /> : <ArrowRight className="h-6 w-6" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* Drawn art for the resource cards */
export const ArtNodes = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="h-full w-full transition-transform duration-500 group-hover:scale-105">
    <rect width="200" height="200" fill="hsl(0 0% 7%)" />
    <g stroke={color} strokeWidth="2" fill="none">
      <path d="M40 60 L100 100 L160 50 M100 100 L70 160 M100 100 L150 150" />
      {[[40, 60], [160, 50], [70, 160], [150, 150]].map(([x, y]) => <circle key={`${x}${y}`} cx={x} cy={y} r="10" fill="hsl(0 0% 7%)" />)}
    </g>
    <rect x="76" y="76" width="48" height="48" rx="14" fill="#fff" />
    <circle cx="100" cy="100" r="9" fill={color} />
  </svg>
);
export const ArtType = ({ top, bottom }: { top: string; bottom: string }) => (
  <div className="flex h-full w-full flex-col items-center justify-center bg-[hsl(0_0%_7%)] text-white transition-transform duration-500 group-hover:scale-105">
    <span className="text-[3.4rem] font-semibold leading-none tracking-[-0.04em]">{top}</span>
    <span className="mt-1 border border-dashed border-white/50 px-2 text-[3.4rem] font-semibold leading-tight tracking-[-0.04em]">{bottom}</span>
  </div>
);
export const ArtShield = ({ bg, color }: { bg: string; color: string }) => (
  <svg viewBox="0 0 200 200" className="h-full w-full transition-transform duration-500 group-hover:scale-105">
    <rect width="200" height="200" fill={bg} />
    <path d="M100 38 L152 58 V104 C152 136 128 156 100 166 C72 156 48 136 48 104 V58 Z" fill={color} opacity="0.25" />
    <path d="M100 56 L138 70 V104 C138 127 121 142 100 150 C79 142 62 127 62 104 V70 Z" fill={color} />
    <path d="M84 102 l12 12 l22 -24" stroke="#fff" strokeWidth="7" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
export const ArtBars = ({ bg, color }: { bg: string; color: string }) => (
  <svg viewBox="0 0 200 200" className="h-full w-full transition-transform duration-500 group-hover:scale-105">
    <rect width="200" height="200" fill={bg} />
    {[0, 1, 2, 3].map((i) => (
      <g key={i}>
        <rect x="40" y={52 + i * 28} width={120 - i * 18} height="14" rx="7" fill={color} opacity={1 - i * 0.2} />
      </g>
    ))}
    <circle cx="150" cy="150" r="22" fill="none" stroke={color} strokeWidth="6" />
    <path d="M166 166 l18 18" stroke={color} strokeWidth="7" strokeLinecap="round" />
  </svg>
);
